import { createAsyncThunk } from '@reduxjs/toolkit';
import leaveApi from '../../api/leaveApi';

// ============================================================================
// THUNKS
// ============================================================================

// ── Fetch employee's own leaves ─────────────────────────────────────────────
export const fetchEmployeeLeaves = createAsyncThunk(
  'leave/fetchEmployeeLeaves',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getEmployeeLeaves();
      // Backend: { success, count, data: [...] }
      const leavesData = response.data.data || response.data;
      const leaves = Array.isArray(leavesData) ? leavesData.map(transformLeave) : [];
      leaves.sort((a, b) => new Date(b.appliedDate) - new Date(a.appliedDate));
      return leaves;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch leaves'
      );
    }
  }
);

// ── Fetch leaves pending the current user's approval ───────────────────────
// Returns { leaves, approvalLevel } so the slice knows which button to show.
export const fetchPendingLeaves = createAsyncThunk(
  'leave/fetchPendingLeaves',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getPendingLeaves();
      // Backend: { success, count, approval_level, data: [...] }
      const body = response.data;
      const leavesData = body.data || [];
      const leaves = Array.isArray(leavesData) ? leavesData.map(transformLeave) : [];
      leaves.sort((a, b) => new Date(b.appliedDate) - new Date(a.appliedDate));
      return {
        leaves,
        approvalLevel: body.approval_level || null, // "ADMIN" | "DEPARTMENT_HEAD" | "HR" | null
        count:         body.count || leaves.length, // backend count — used for badge, avoids array.length
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch pending leaves'
      );
    }
  }
);

// ── Fetch all leaves (HR / admin list with filters) ────────────────────────
export const fetchAllLeaves = createAsyncThunk(
  'leave/fetchAllLeaves',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.employee)   params.employee_id = filters.employee;
      if (filters.fromDate)   params.from_date   = filters.fromDate;
      if (filters.toDate)     params.to_date     = filters.toDate;
      if (filters.leaveType)  params.leave_type  = filters.leaveType;

      const response = await leaveApi.getAllLeaves(params);
      const leavesData = response.data.data || response.data;
      const leaves = Array.isArray(leavesData) ? leavesData.map(transformLeave) : [];
      leaves.sort((a, b) => new Date(b.appliedDate) - new Date(a.appliedDate));
      return leaves;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch all leaves'
      );
    }
  }
);

// ── Apply for leave ─────────────────────────────────────────────────────────
export const applyLeave = createAsyncThunk(
  'leave/applyLeave',
  async (leaveData, { rejectWithValue }) => {
    try {
      const response = await leaveApi.applyLeave(leaveData);
      // Backend: { success, message, leave: { id, status, total_days, ... }, approvers }
      const body = response.data;
      // Build a minimal leave object the transform function can handle
      const rawLeave = {
        id:             body.leave?.id,
        status:         body.leave?.status,       // 'PENDING_DEPT_HEAD'
        total_days:     body.leave?.total_days,
        leave_type:     leaveData.leave_type,
        planning_type:  leaveData.planning_type || 'PLANNED',
        duration:       leaveData.duration       || 'FULL_DAY',
        from_date:      leaveData.from_date,
        to_date:        leaveData.to_date,
        reason:         leaveData.reason,
        approvers:      body.approvers,           // pass through for UI
      };
      return transformLeave(rawLeave);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to apply for leave'
      );
    }
  }
);

// ── Department Head Approval (FIRST LEVEL) ─────────────────────────────────
export const deptHeadApproval = createAsyncThunk(
  'leave/deptHeadApproval',
  async ({ leaveId, action, remark = '' }, { rejectWithValue }) => {
    try {
      const response = await leaveApi.deptHeadApproval(leaveId, action, remark);
      // Backend: { success, message, leave: { id, status, dept_head_approved, requires_hr_approval } }
      const body = response.data;
      return {
        leaveId,
        action,
        message: body.message,
        leave:   body.leave,  // { id, status, dept_head_approved, requires_hr_approval }
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to process department head approval'
      );
    }
  }
);

// ── HR Approval (SECOND LEVEL) ──────────────────────────────────────────────
export const hrApproval = createAsyncThunk(
  'leave/hrApproval',
  async ({ leaveId, action, remark = '' }, { rejectWithValue }) => {
    try {
      const response = await leaveApi.hrApproval(leaveId, action, remark);
      // Backend: { success, message, leave: { id, status, dept_head_approved, hr_approved, fully_approved } }
      const body = response.data;
      return {
        leaveId,
        action,
        message: body.message,
        leave:   body.leave,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to process HR approval'
      );
    }
  }
);

// ── Fetch leave balance ─────────────────────────────────────────────────────
export const fetchLeaveBalance = createAsyncThunk(
  'leave/fetchLeaveBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getLeaveBalance();
      const d = response.data.data || response.data;
      return {
        casual:  d.casual  || 0,
        medical: d.medical || 0,
        earned:  d.earned  || 0,
        total:   (d.casual || 0) + (d.medical || 0) + (d.earned || 0),
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch leave balance'
      );
    }
  }
);

// ── Fetch leave summary ─────────────────────────────────────────────────────
export const fetchLeaveSummary = createAsyncThunk(
  'leave/fetchLeaveSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getLeaveSummary();
      const d = response.data.data || response.data;
      return {
        totalLeaves: d.total     || 0,
        pending:     d.pending   || 0,
        approved:    d.approved  || 0,
        rejected:    d.rejected  || 0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch leave summary'
      );
    }
  }
);

// ============================================================================
// TRANSFORM — maps one backend Leave object → frontend shape
// ============================================================================

/**
 * The backend returns status in different forms depending on the endpoint:
 *
 *  /leaves/my-leaves/          → MyLeaveSerializer
 *    status = "PENDING" | "APPROVED" | "REJECTED"  (friendly, no raw_status)
 *
 *  /leaves/  (list, admin/HR)  → LeaveListSerializer
 *    status    = "PENDING" | "APPROVED" | "REJECTED"  (friendly)
 *    raw_status = "PENDING_DEPT_HEAD" | "PENDING_HR" | "APPROVED" |
 *                 "REJECTED_BY_DEPT_HEAD" | "REJECTED_BY_HR"
 *
 *  /leaves/{id}/               → LeaveDetailSerializer
 *  /leaves/pending-for-approval/ → LeaveDetailSerializer
 *    status = raw DB value (PENDING_DEPT_HEAD, REJECTED_BY_HR, …)
 *             no raw_status field
 *
 * Strategy:
 *  - rawStatus  → raw DB value when we have it; null when only friendly available
 *  - status     → always the friendly value ("PENDING" / "APPROVED" / "REJECTED")
 *  - displayStatus → detailed label when rawStatus known; friendly label otherwise
 */

// All possible raw DB status values
const RAW_STATUS_SET = new Set([
  'PENDING_DEPT_HEAD',
  'PENDING_HR',
  'APPROVED',
  'REJECTED_BY_DEPT_HEAD',
  'REJECTED_BY_HR',
]);

/** Map a raw DB status → friendly UI status */
const toFriendlyStatus = (raw) => {
  if (raw === 'PENDING_DEPT_HEAD' || raw === 'PENDING_HR') return 'PENDING';
  if (raw === 'APPROVED')                                   return 'APPROVED';
  if (raw === 'REJECTED_BY_DEPT_HEAD' || raw === 'REJECTED_BY_HR') return 'REJECTED';
  return raw; // pass-through for unknown values
};

const transformLeave = (leave) => {
  if (!leave) return null;

  const inputStatus = leave.status || '';

  // Resolve rawStatus: prefer explicit raw_status field (from LeaveListSerializer),
  // then fall back to the input if it looks like a raw DB value (from LeaveDetailSerializer).
  // If only a friendly value came in (from MyLeaveSerializer), rawStatus = null.
  const rawStatus =
    leave.raw_status ||
    (RAW_STATUS_SET.has(inputStatus) ? inputStatus : null);

  // Friendly status is always "PENDING" | "APPROVED" | "REJECTED"
  const friendlyStatus = rawStatus ? toFriendlyStatus(rawStatus) : inputStatus;

  // Detailed human label: granular when rawStatus is known, friendly label otherwise
  const displayStatus = rawStatus ? mapDisplayStatus(rawStatus) : friendlyStatus;

  return {
    id:             leave.id,
    employeeId:     leave.employee || leave.employee_id,
    employeeName:   leave.employee_name || '',
    employeeCode:   String(leave.employee_code || ''),
    department:     leave.department || '',

    // Core fields (backend keys are snake_case)
    leaveType:      leave.leave_type    || 'CASUAL',
    planningType:   leave.planning_type || 'PLANNED',
    duration:       leave.duration      || 'FULL_DAY',
    fromDate:       leave.from_date,
    toDate:         leave.to_date,
    appliedDate:    leave.created_at || new Date().toISOString(),
    reason:         leave.reason || '',
    totalDays:      leave.total_days || calculateDays(leave),

    // ── Status fields ──────────────────────────────────────────────────────
    status:        friendlyStatus,   // always "PENDING" | "APPROVED" | "REJECTED"
    rawStatus,                       // raw DB value or null; use for precise comparisons
    displayStatus,                   // e.g. "Pending Dept Head", "Rejected by HR"

    // ── Two-level approval state ───────────────────────────────────────────
    // NOTE: deptHeadApproved / hrApproved are only present when the backend
    //       returns LeaveDetailSerializer or LeaveListSerializer (not MyLeaveSerializer).
    deptHeadApproved:   !!leave.dept_head_approved,
    deptHeadApprovedBy: leave.dept_head_approved_by || null,
    deptHeadApprovedAt: leave.dept_head_approved_at || null,
    deptHeadRemark:     leave.dept_head_remark      || '',

    hrApproved:         !!leave.hr_approved,
    hrApprovedBy:       leave.hr_approved_by || null,
    hrApprovedAt:       leave.hr_approved_at || null,
    hrRemark:           leave.hr_remark      || '',

    // ── Convenience flags (always reliable regardless of status shape) ─────
    isPending:          friendlyStatus === 'PENDING',
    isApproved:         friendlyStatus === 'APPROVED',
    isRejected:         friendlyStatus === 'REJECTED',

    // These are only non-null when rawStatus is known
    requiresDeptHead:     rawStatus === 'PENDING_DEPT_HEAD',
    requiresHR:           rawStatus === 'PENDING_HR',
    rejectedByDeptHead:   rawStatus === 'REJECTED_BY_DEPT_HEAD',
    rejectedByHR:         rawStatus === 'REJECTED_BY_HR',

    // Pass-through (only present on create response)
    approvers:          leave.approvers || null,
  };
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Map raw backend status → a display-friendly label.
 * Friendly values ("PENDING", "APPROVED", "REJECTED") are returned as-is.
 */
const mapDisplayStatus = (status) => {
  const map = {
    'PENDING_DEPT_HEAD':     'Pending Dept Head',
    'PENDING_HR':            'Pending HR',
    'APPROVED':              'Approved',
    'REJECTED_BY_DEPT_HEAD': 'Rejected by Dept Head',
    'REJECTED_BY_HR':        'Rejected by HR',
    // Friendly pass-throughs
    'PENDING':   'Pending',
    'REJECTED':  'Rejected',
  };
  return map[status] || status || 'Unknown';
};

/**
 * Calculate days from date range when total_days is not supplied.
 */
const calculateDays = (leave) => {
  if (leave.total_days) return leave.total_days;

  const from = new Date(leave.from_date || leave.fromDate);
  const to   = new Date(leave.to_date   || leave.toDate);
  if (isNaN(from) || isNaN(to)) return 1;

  const days = Math.ceil(Math.abs(to - from) / (1000 * 60 * 60 * 24)) + 1;
  return leave.duration === 'HALF_DAY' ? days * 0.5 : days;
};

// ============================================================================
// LABEL HELPERS — used by UI components
// ============================================================================

export const LEAVE_TYPE_LABELS = {
  CASUAL:  'Casual Leave',
  MEDICAL: 'Medical Leave',
  EARNED:  'Earned Leave',
};

export const PLANNING_TYPE_LABELS = {
  PLANNED:      'Planned',
  UNPLANNED:    'Unplanned',
  LAST_MINUTE:  'Last Minute',
};

export const DURATION_LABELS = {
  FULL_DAY:  'Full Day',
  HALF_DAY:  'Half Day',
};

export const STATUS_LABELS = {
  PENDING_DEPT_HEAD:     'Pending Dept Head',
  PENDING_HR:            'Pending HR',
  APPROVED:              'Approved',
  REJECTED_BY_DEPT_HEAD: 'Rejected by Dept Head',
  REJECTED_BY_HR:        'Rejected by HR',
};

/** Generic label lookup */
export const getLabel = (map, key) => map[key] || key;

export const getLeaveTypeLabel  = (type)     => getLabel(LEAVE_TYPE_LABELS,    type);
export const getPlanningLabel   = (planning) => getLabel(PLANNING_TYPE_LABELS, planning);
export const getDurationLabel   = (dur)      => getLabel(DURATION_LABELS,      dur);
export const getStatusLabel     = (status)   => getLabel(STATUS_LABELS,        status);